import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { VerificationBadge } from "@/pages/AskTheCandidates";
import { Scale, ExternalLink, Columns3 } from "lucide-react";

/** Phase 11.4 — evidence-first candidate comparison.
 * Shows candidates' ACTUAL answers, sources and response states side by side.
 * HARD RULE: no scores, star ratings, rankings, recommendations or AI judgements. */

const choiceLabel = (c) => (c === "yes" ? "Yes" : c === "no" ? "No" : "Other");
const fmtDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return iso; }
};

const CellState = ({ item }) => {
  if (!item) return <span className="text-xs text-muted-foreground">—</span>;
  if (item.status === "answered") {
    return (
      <div>
        {item.choice && (
          <Badge variant="outline" className="bg-secondary text-foreground border-slate-300 font-semibold mb-1">
            {choiceLabel(item.choice)}
          </Badge>
        )}
        <p className="text-xs text-foreground whitespace-pre-wrap leading-5">
          {item.answer || item.explanation}
        </p>
        <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
          {item.was_updated && (
            <span className="text-[10px] text-sky-800">Updated {fmtDate(item.updated_at)}</span>
          )}
          {item.source_url && (
            <a href={item.source_url} target="_blank" rel="noopener noreferrer"
               className="text-[10px] text-primary underline underline-offset-2 inline-flex items-center gap-0.5">
              Source <ExternalLink className="h-2.5 w-2.5" />
            </a>
          )}
        </div>
      </div>
    );
  }
  if (item.status === "no_response") {
    return <span className="text-xs text-muted-foreground italic">Invited — No Response Yet</span>;
  }
  if (item.status === "not_invited") {
    return <span className="text-xs text-muted-foreground italic">Not Yet Invited</span>;
  }
  if (item.status === "withheld") {
    return <span className="text-xs text-amber-800">{item.note}</span>;
  }
  return <span className="text-xs text-muted-foreground">—</span>;
};

export default function CompareCandidates() {
  const [params, setParams] = useSearchParams();
  const [munis, setMunis] = useState([]);
  const [muniId, setMuniId] = useState(params.get("municipality") || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/public/municipalities").then((r) => {
      setMunis(r.data);
      if (!muniId && r.data.length === 1) setMuniId(r.data[0].id);
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!muniId) return;
    setLoading(true);
    setParams({ municipality: muniId }, { replace: true });
    api.get("/public/qa/comparison", { params: { municipality_id: muniId } })
      .then((r) => setData(r.data)).catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [muniId]); // eslint-disable-line react-hooks/exhaustive-deps

  const cands = data?.candidates || [];

  return (
    <div className="wyv-container py-10 sm:py-14">
      <Badge variant="outline" className="bg-blue-50 text-blue-800 border-blue-200 mb-3">Candidate Q&A Corner</Badge>
      <h1 className="font-heading font-bold text-3xl sm:text-4xl text-foreground" data-testid="compare-title">
        Compare the Candidates
      </h1>
      <p className="text-muted-foreground mt-2 max-w-2xl">
        Every candidate's actual answers, side by side — the identical questions, the identical limits,
        the identical treatment. WitnessYourVote never scores, ranks or recommends candidates:
        read their words and decide for yourself.
      </p>
      <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 p-3.5" data-testid="compare-non-endorsement">
        <p className="text-xs text-blue-900 inline-flex items-start gap-2">
          <Scale className="h-4 w-4 shrink-0 mt-0.5" />
          Candidate answers are provided by the candidates themselves. WitnessYourVote does not endorse
          candidates or score their responses.
        </p>
      </div>

      <div className="w-full sm:w-72 mt-6">
        <Label className="text-xs text-muted-foreground">Municipality</Label>
        <Select value={muniId || undefined} onValueChange={setMuniId}>
          <SelectTrigger className="mt-1 bg-white" data-testid="compare-muni-select"><SelectValue placeholder="Choose your municipality" /></SelectTrigger>
          <SelectContent className="bg-white">
            {munis.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading && <Skeleton className="h-96 mt-6" />}

      {data && !loading && cands.length === 0 && (
        <Card className="mt-6" data-testid="compare-empty">
          <CardContent className="p-8 text-center">
            <Columns3 className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
            <p className="font-heading font-semibold text-foreground">No participating candidates yet</p>
            <p className="text-sm text-muted-foreground mt-1">
              When candidates are invited to community questions, their answers appear here side by side.
            </p>
          </CardContent>
        </Card>
      )}

      {data && !loading && cands.length > 0 && (
        <Card className="mt-6" data-testid="compare-table-card">
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-left border-collapse min-w-[640px]" data-testid="compare-table">
              <thead>
                <tr className="border-b border-slate-200 bg-secondary/60">
                  <th className="p-4 text-xs font-bold uppercase tracking-wide text-muted-foreground w-52 align-bottom">
                    Question
                  </th>
                  {cands.map((c) => (
                    <th key={c.candidate.id} className="p-4 align-bottom min-w-56" data-testid={`compare-col-${c.candidate.slug}`}>
                      <p className="font-heading font-semibold text-sm text-foreground">{c.candidate.name}</p>
                      <div className="mt-1"><VerificationBadge status={c.candidate.verification_status} /></div>
                      {c.candidate.campaign_website && (
                        <a href={c.candidate.campaign_website} target="_blank" rel="noopener noreferrer"
                           className="text-[10px] text-primary underline underline-offset-2 inline-flex items-center gap-0.5 mt-1">
                          Campaign website <ExternalLink className="h-2.5 w-2.5" />
                        </a>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.questions.map((q) => (
                  <tr key={q.id} className="border-b border-slate-100 align-top">
                    <td className="p-4 text-sm font-semibold text-foreground">{q.question}</td>
                    {cands.map((c) => (
                      <td key={c.candidate.id} className="p-4 align-top">
                        <CellState item={c.qa.find((i) => i.question_id === q.id)} />
                      </td>
                    ))}
                  </tr>
                ))}
                {data.transparency_questions.map((tq) => (
                  <tr key={tq.key} className="border-b border-slate-100 align-top">
                    <td className="p-4">
                      <p className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Election Transparency</p>
                      <p className="text-sm font-semibold text-foreground mt-0.5">{tq.title}</p>
                      <p className="text-[11px] text-muted-foreground mt-0.5">{tq.question}</p>
                    </td>
                    {cands.map((c) => (
                      <td key={c.candidate.id} className="p-4 align-top" data-testid={`compare-cell-${tq.key}-${c.candidate.slug}`}>
                        <CellState item={c.transparency.find((i) => i.key === tq.key)} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      <p className="text-[11px] text-muted-foreground italic mt-4" data-testid="compare-evidence-note">
        {data?.evidence_first_note || "WitnessYourVote shows candidates' actual answers and sources. It never scores, ranks or recommends candidates."}
        {" "}See the full Q&A on <Link to="/ask-the-candidates" className="text-primary underline underline-offset-2">Ask the Candidates</Link>.
      </p>
    </div>
  );
}
